import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { Router, RouterLink } from '@angular/router';
import { MockingClaimsService } from '../../../../services/mocking-claims.service';
import {environment} from "../../../../../environments/environment";


const endpoint = environment.backendApi + "/api/claim/";


@Component({
  selector: 'app-mocking',
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="mocking-container">
      <h2>JSON Data Mocking Tool</h2>

      <div class="form-section">
        <label for="endpoint">Claim Endpoint:</label>
        <select id="endpoint" [(ngModel)]="selectedEndpoint" class="endpoint-select">
          <option value="">Select an endpoint...</option>
          <option value="submit">Submit Claim</option>
          <option value="update">Update Claim</option>
          <option value="validate">Validate Claim</option>
        </select>
      </div>

      <div class="form-section">
        <label for="jsonData">JSON Data:</label>
        <textarea
          id="jsonData"
          [(ngModel)]="jsonData"
          class="json-textarea"
          placeholder="Paste your JSON data here..."
          rows="15">
        </textarea>
      </div>

      <div class="button-section">
        <button
          (click)="sendRequest()"
          [disabled]="!selectedEndpoint || !jsonData"
          class="send-button">
          Send Request
        </button>
        <button
          (click)="clearData()"
          class="clear-button">
          Clear
        </button>
        <button
          (click)="formatJson()"
          [disabled]="!jsonData"
          class="format-button">
          Format JSON
        </button>
        <button
          (click)="loadMockData('standard')"
          class="mock-button">
          Load Standard Mock
        </button>
        <button
          (click)="loadMockData('minimal')"
          class="mock-button">
          Load Minimal Mock
        </button>
        <button
          (click)="loadMockData('complex')"
          class="mock-button">
          Load Complex Mock
        </button>
        <a
          routerLink=""
          class="home-button">
          Go to Home
        </a>
      </div>

      <div class="response-section" *ngIf="response">
        <h3>Response:</h3>
        <div class="response-container" [class.error]="isError">
          <pre>{{ response }}</pre>
        </div>
      </div>

      <div class="status-section" *ngIf="loading">
        <p>Sending request...</p>
      </div>
    </div>
  `,
  styles: [`
    .mocking-container {
      max-width: 1000px;
      margin: 0 auto;
      padding: 20px;
    }

    h2 {
      color: #333;
      margin-bottom: 30px;
    }

    .form-section {
      margin-bottom: 20px;
    }

    label {
      display: block;
      margin-bottom: 5px;
      font-weight: 600;
      color: #555;
    }

    .endpoint-select {
      width: 100%;
      padding: 8px 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 14px;
    }

    .json-textarea {
      width: 100%;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      resize: vertical;
      min-height: 300px;
    }

    .button-section {
      margin-bottom: 30px;
    }

    .send-button,
    .clear-button,
    .format-button {
      padding: 10px 20px;
      margin-right: 10px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
    }

    .send-button {
      background-color: #007bff;
      color: white;
    }

    .send-button:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    .clear-button {
      background-color: #6c757d;
      color: white;
    }

    .format-button,
    .mock-button {
      background-color: #28a745;
      color: white;
    }

    .format-button:disabled,
    .mock-button:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    .mock-button {
      background-color: #17a2b8;
    }

    .home-button {
      background-color: #6f42c1;
      color: white;
      padding: 10px 20px;
      margin-right: 10px;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      font-weight: 600;
      text-decoration: none;
      display: inline-block;
    }

    .home-button:hover {
      background-color: #5a2d91;
      color: white;
    }

    .response-section {
      margin-top: 30px;
    }

    h3 {
      color: #333;
      margin-bottom: 10px;
    }

    .response-container {
      background-color: #f8f9fa;
      border: 1px solid #dee2e6;
      border-radius: 4px;
      padding: 15px;
      max-height: 400px;
      overflow-y: auto;
    }

    .response-container.error {
      background-color: #f8d7da;
      border-color: #f5c6cb;
      color: #721c24;
    }

    pre {
      margin: 0;
      font-family: 'Courier New', monospace;
      font-size: 13px;
      white-space: pre-wrap;
      word-wrap: break-word;
    }

    .status-section {
      color: #007bff;
      font-weight: 600;
    }
  `]
})
export class MockingComponent implements OnInit {
  private http = inject(HttpClient);
  private router = inject(Router);
  private mockingService = inject(MockingClaimsService);

  selectedEndpoint = 'submit';
  jsonData = '';
  response = '';
  loading = false;
  isError = false;

  ngOnInit() {
    // Pre-fill with standard mock data
    this.loadMockData('standard');
  }

  sendRequest() {
    if (!this.selectedEndpoint || !this.jsonData) {
      return;
    }

    this.loading = true;
    this.response = '';
    this.isError = false;

    try {
      const parsedData = JSON.parse(this.jsonData);

      // this.http.post(endpoint + this.selectedEndpoint, parsedData).subscribe({
      this.http.post(endpoint, parsedData).subscribe({
        next: (response) => {
          this.loading = false;
          this.response = JSON.stringify(response, null, 2);
          this.isError = false;
        },
        error: (error) => {
          this.loading = false;
          this.response = JSON.stringify(error, null, 2);
          this.isError = true;
        }
      });
    } catch (error) {
      this.loading = false;
      this.response = `JSON Parse Error: ${error}`;
      this.isError = true;
    }
  }

  clearData() {
    this.jsonData = '';
    this.response = '';
    this.selectedEndpoint = '';
    this.isError = false;
  }

  formatJson() {
    if (!this.jsonData) {
      return;
    }

    try {
      const parsed = JSON.parse(this.jsonData);
      this.jsonData = JSON.stringify(parsed, null, 2);
    } catch (error) {
      alert('Invalid JSON format. Please check your data.');
    }
  }

  loadMockData(type: 'standard' | 'minimal' | 'complex') {
    switch (type) {
      case 'standard':
        this.jsonData = this.mockingService.getMockClaimReportAsJson();
        break;
      case 'minimal':
        this.jsonData = JSON.stringify(this.mockingService.getMinimalMockClaimReport(), null, 2);
        break;
      case 'complex':
        this.jsonData = JSON.stringify(this.mockingService.getComplexMockClaimReport(), null, 2);
        break;
    }
    this.response = '';
    this.isError = false;
  }

}
